import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import Form, { FormSection, FormHeader, FormFooter, HelperMessage } from '@atlaskit/form';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  ErrorComponents,
  EditCreateInvoiceGroup,
  NotificationTypes,
  successNotificationBuilder,
} from '@css-xp/base';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import { CommerceAddress } from '@css-xp/graphtypes';

interface FormData {
  recipients: Array<string>;
}

interface Props {
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  invoiceGroupId: string;
  recipients: Array<string>;
  shipToParty: CommerceAddress;
}
export const EditInvoiceRecipientPane = ({
  recipients,
  invoiceGroupId,
  refetch,
  transactionAccountId,
  shipToParty,
}: Props): JSX.Element => {
  const [adminFieldsDirty, setAdminFieldsDirty] = React.useState<boolean>(true);
  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceGroupRequest, any>({
    submitFormAction: updateInvoiceGroup,
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const updateAdminFieldsDirtyFlag = (flag: boolean) => {
    setAdminFieldsDirty(flag);
  };

  const handleFormSubmit = (formData: FormData) => {
    const requestData: UpdateInvoiceGroupRequest = {
      data: {
        recipients: (formData?.recipients ?? []).filter((recipient: string) => recipient.length > 0),
        shipToPartyId: shipToParty.id,
      },
      invoiceGroupId,
      transactionAccountId,
    };
    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-invoice-group-success')
      .withHeader('Successfully updated invoice group.')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  return (
    <div style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader title="Invoice recipients"></FormHeader>
            <HelperMessage>Manage the invoice recipients for this billing profile.</HelperMessage>
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the recipients for the following reason"
                      message={formError}
                    />
                  }
                />
              ) : null}
            </FormSection>

            <FormSection>
              <EditCreateInvoiceGroup
                updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                recipients={recipients ?? ['']}
                isRequired={false}
                placeholder={'Enter email address of the invoice recipient'}
                helperMessage={''}
                title={''}
              />
            </FormSection>

            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  isDisabled={!dirty && !adminFieldsDirty}
                  appearance="primary"
                  type="submit"
                  testId="update-button"
                  isLoading={isProcessing}
                >
                  Save
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(EditInvoiceRecipientPane);
